import { Moon, Sun } from "lucide-react";
import { Suspense, type ReactNode } from "react";
import { useGetIdentity } from "ra-core";
import { Link, useLocation, useMatch } from "react-router";
import { useFadeOnChange } from "@/hooks/useFadeOnChange";
import { ErrorBoundary } from "react-error-boundary";
import { UserMenu } from "@/components/admin/user-menu";
import { useTheme } from "@/components/admin/use-theme";
import { Error } from "@/components/admin/error";
import { Notification } from "@/components/admin/notification";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sidebar,
  SidebarFooter,
  SidebarHeader,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useMemberRoleLabel } from "@/hooks/useMemberRoleLabel";
import { BrandWordmark } from "./BrandWordmark";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { useConfigurationLoader } from "../root/useConfigurationLoader";
import { CRMUserMenuItems } from "./UserMenuItems";
import { DealsExplorerPanel } from "../deals/DealsExplorerPanel";
import { LbsSidebarNav } from "@/app/LbsSidebarNav";
import { useWebsiteMonitorEnabled } from "@/modules/settings/useWebsiteMonitorSettings";
import { useMessagesUnreadCounts } from "@/modules/messages/useMessagesUnreadCounts";
import { useMailUnreadCount } from "@/modules/mail/useMailUnreadCount";
import { useNotificationUnreadCounts } from "@/modules/notifications/useNotificationUnreadCounts";
import {
  PageActionsProvider,
  PageActionsSlot,
  PageActionsTrailingSlot,
} from "@/components/atomic-crm/layout/PageActions";
import { CrmAssistantButton } from "@/modules/assistant";
import { SpotlightSearchButton } from "@/components/atomic-crm/layout/SpotlightSearchButton";
import { GlobalQuickCreateMenu } from "@/components/atomic-crm/layout/GlobalQuickCreateMenu";
import { GlobalCommsHeaderActions } from "@/components/atomic-crm/layout/GlobalCommsHeaderActions";
import { NotificationCenterButton } from "@/modules/notifications/NotificationCenterButton";
import {
  isProposalFocusModePath,
  isProposalPreviewPath,
} from "@/modules/proposals/proposalFocusMode";
import { isBillingInvoiceWorkspace } from "@/modules/billing/billingWorkspaceMode";
import { isBillingSubscriptionWorkspace } from "@/modules/billing/subscriptions/billingNavigation";
import { WorkspaceTabBar } from "@/modules/workspace/WorkspaceChrome";
import { useWorkspaceTabBarVisible } from "@/modules/workspace/workspaceTabsContext";

const SidebarThemeSwitcher = ({ collapsed }: { collapsed: boolean }) => {
  const { theme, setTheme } = useTheme();
  const activeTheme = theme === "dark" ? "dark" : "light";

  if (collapsed) {
    const nextTheme = activeTheme === "dark" ? "light" : "dark";
    return (
      <IconButton
        className="h-8 w-8"
        aria-label="Toggle theme"
        onClick={() => setTheme(nextTheme)}
      >
        {activeTheme === "dark" ? (
          <Moon className="h-4 w-4" />
        ) : (
          <Sun className="h-4 w-4" />
        )}
        <span className="sr-only">Toggle theme</span>
      </IconButton>
    );
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setTheme("light")}
        className={cn(
          "h-8 flex-1 justify-center gap-1.5 px-2 text-xs",
          activeTheme === "light" &&
            "bg-sidebar-accent text-sidebar-accent-foreground",
        )}
      >
        <Sun className="h-4 w-4" />
        <span>Light</span>
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setTheme("dark")}
        className={cn(
          "h-8 flex-1 justify-center gap-1.5 px-2 text-xs",
          activeTheme === "dark" &&
            "bg-sidebar-accent text-sidebar-accent-foreground",
        )}
      >
        <Moon className="h-4 w-4" />
        <span>Dark</span>
      </Button>
    </div>
  );
};

const SidebarUserIdentity = ({ collapsed }: { collapsed: boolean }) => {
  const { data: identity } = useGetIdentity();
  const roleLabel = useMemberRoleLabel();
  const fullName = identity?.fullName ?? "User";
  const triggerClassName = collapsed
    ? "h-10 w-10 rounded-full p-0"
    : "h-auto w-full justify-start rounded-md p-2";

  return (
    <UserMenu
      trigger={
        <Button type="button" variant="ghost" className={triggerClassName}>
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarImage src={(identity as any)?.avatar} role="presentation" />
            <AvatarFallback>{fullName.charAt(0)}</AvatarFallback>
          </Avatar>
          {!collapsed ? (
            <span className="min-w-0 text-left">
              <span className="block truncate text-sm font-medium">
                {fullName}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {roleLabel}
              </span>
            </span>
          ) : null}
        </Button>
      }
    >
      <CRMUserMenuItems />
    </UserMenu>
  );
};

const SidebarFooterControls = () => {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  return (
    <div
      className={cn(
        "p-1",
        collapsed
          ? "flex flex-col items-center justify-center gap-2"
          : "space-y-2",
      )}
    >
      <SidebarUserIdentity collapsed={collapsed} />
      <SidebarThemeSwitcher collapsed={collapsed} />
    </div>
  );
};

const SidebarNavigation = () => {
  const { totalUnread: messagesUnreadCount } = useMessagesUnreadCounts();
  const { data: mailUnreadCount = 0 } = useMailUnreadCount();
  const { tickets: ticketsNotificationUnread } = useNotificationUnreadCounts();
  const { enabled: websiteMonitorEnabled } = useWebsiteMonitorEnabled(true);
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();

  return (
    // 16px from the window on top, left and bottom; the page adds the
    // 16px between the sidebar and the content (same gutter everywhere).
    <Sidebar
      variant="floating"
      collapsible="icon"
      className="p-4 pr-0 print:hidden"
    >
      <SidebarHeader className="px-2 pt-2 pb-0">
        <div className="relative">
          <SidebarMenu className="group-data-[collapsible=icon]:hidden">
            <SidebarMenuItem>
              <SidebarMenuButton asChild className="h-auto py-2 pr-8">
                <Link to="/" className="gap-2">
                  <img
                    className="[.light_&]:hidden h-6 w-6 rounded-sm object-cover"
                    src={darkModeLogo}
                    alt={title}
                  />
                  <img
                    className="[.dark_&]:hidden h-6 w-6 rounded-sm object-cover"
                    src={lightModeLogo}
                    alt={title}
                  />
                  <BrandWordmark title={title} titleClassName="text-base" />
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          <div className="relative hidden h-10 items-center justify-center group-data-[collapsible=icon]:flex">
            <Link
              to="/"
              className="absolute inset-0 flex items-center justify-center transition-[opacity,transform] duration-150 ease-out group-hover:opacity-0 group-hover:scale-95"
            >
              <img
                className="[.light_&]:hidden h-6 w-6 rounded-sm object-cover"
                src={darkModeLogo}
                alt={title}
              />
              <img
                className="[.dark_&]:hidden h-6 w-6 rounded-sm object-cover"
                src={lightModeLogo}
                alt={title}
              />
            </Link>
            <SidebarTrigger
              className="absolute inset-0 m-auto opacity-0 pointer-events-none transition-[opacity,transform] duration-150 ease-out scale-95 group-hover:scale-100 group-hover:opacity-100 group-hover:pointer-events-auto"
              variant="ghost"
              size="icon"
            />
          </div>
          <SidebarTrigger
            className="absolute top-1.5 right-1.5 opacity-100 transition-opacity group-data-[collapsible=icon]:hidden"
            variant="ghost"
            size="icon"
          />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <LbsSidebarNav
          websiteMonitorEnabled={websiteMonitorEnabled}
          messagesUnreadCount={messagesUnreadCount}
          ticketsNotificationUnread={ticketsNotificationUnread}
          mailUnreadCount={mailUnreadCount}
        />
      </SidebarContent>
      <SidebarFooter className="p-2 group-data-[collapsible=icon]:p-1">
        <SidebarFooterControls />
      </SidebarFooter>
    </Sidebar>
  );
};

export const SidebarLayout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  const location = useLocation();
  // Fade the page in only when the module changes (/tickets → /billing).
  // Opening another item inside a module (/tickets/1 → /tickets/2) keeps
  // the page still; that item's own pane fades instead.
  const pageRef = useFadeOnChange<HTMLDivElement>(
    location.pathname.split("/")[1] ?? "",
  );
  const matchDealShow = useMatch("/deals/:id/show");
  const matchMessages = useMatch("/messages");
  const isSettingsShell = Boolean(useMatch("/settings"));
  const currentDealId = matchDealShow?.params.id;
  const isMessagesShell = Boolean(matchMessages);
  const isProposalFocusMode = isProposalFocusModePath(location.pathname);
  const isProposalPreview = isProposalPreviewPath(location.pathname);
  // Open invoice or subscription: fixed chrome, panes scroll inside.
  const isBillingInvoiceShell =
    isBillingInvoiceWorkspace(location.pathname, location.search) ||
    isBillingSubscriptionWorkspace(location.pathname, location.search);
  const hideGlobalSearch = isProposalFocusMode;
  const hideGlobalHeader = isProposalPreview;
  const showDealExplorer =
    Boolean(currentDealId) && !isMessagesShell && !isProposalPreview;
  const isProjectShowShell = showDealExplorer;
  /**
   * Only the client-facing proposal preview is full-bleed (no top bar →
   * FAB Ask Sigma + ⌘K listener still mounted). Projects keep the top bar
   * like every other page.
   */
  const shellHidesGlobalHeader = hideGlobalHeader;

  // The top bar is a card like everything else: 16px from the window and
  // the sidebar, 16px above the page. Open tabs live in that card. A crisp
  // border, no shadow (docs/ux/ELEVATION.md).
  const headerCardClass =
    "mx-4 mt-4 flex shrink-0 flex-col rounded-xl border bg-card print:hidden";
  const headerRowClass = "flex h-11 items-center gap-3 pr-2 pl-4";
  // Wide screens: tabs share the row with the page actions (one slim bar).
  // Narrow screens: tabs get their own row under it.
  const tabsVisible = useWorkspaceTabBarVisible();
  const pageActionsClass = cn(
    "flex min-w-0 flex-1 items-center gap-2 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
    tabsVisible && "min-[1100px]:max-w-[40%] min-[1100px]:flex-none",
  );

  const globalHeader = shellHidesGlobalHeader ? null : !hideGlobalSearch ? (
    <header className={headerCardClass}>
      <div className={headerRowClass}>
        <PageActionsSlot className={pageActionsClass} />
        <WorkspaceTabBar
          placement="inline"
          className="hidden min-[1100px]:flex"
        />
        <div className="flex shrink-0 items-center gap-2">
          <PageActionsTrailingSlot className="flex items-center" />
          <GlobalQuickCreateMenu />
          <GlobalCommsHeaderActions />
          <CrmAssistantButton />
          <SpotlightSearchButton />
          <NotificationCenterButton />
        </div>
      </div>
      <WorkspaceTabBar placement="header" className="min-[1100px]:hidden" />
    </header>
  ) : (
    <header className={headerCardClass}>
      <div className={headerRowClass}>
        <PageActionsSlot className={pageActionsClass} />
        <WorkspaceTabBar
          placement="inline"
          className="hidden min-[1100px]:flex"
        />
        <PageActionsTrailingSlot className="ml-auto flex items-center" />
        <GlobalQuickCreateMenu />
        <GlobalCommsHeaderActions />
        <CrmAssistantButton />
        <SpotlightSearchButton variant="hidden" />
        <NotificationCenterButton />
      </div>
      <WorkspaceTabBar placement="header" className="min-[1100px]:hidden" />
    </header>
  );

  const mainContentPadding = cn(
    "flex min-h-0 flex-1 flex-col print:block print:px-0 print:pt-0 print:pb-0",
    isMessagesShell
      ? "gap-0 p-4"
      : isSettingsShell
        ? "gap-0 p-0 overflow-hidden"
        : isProposalPreview
          ? "gap-0 p-0"
          : isBillingInvoiceShell
            ? "gap-0 p-4"
            : showDealExplorer
              ? "flex min-h-0 flex-1 flex-col p-4"
              : "gap-0 p-0",
  );

  const scrollableContent = (
    <div
      ref={pageRef}
      className={cn(
        "min-h-0 min-w-0 flex-1",
        isMessagesShell ||
          isProposalPreview ||
          isBillingInvoiceShell ||
          isProjectShowShell
          ? "overflow-hidden"
          : isSettingsShell
            ? "overflow-y-auto overscroll-contain px-4 pt-3 pb-4"
            : // Same 16px inset on all four sides, inside the scroll area so
              // the bottom keeps its gap when the page scrolls.
              "overflow-y-auto overscroll-contain px-4 pt-3 pb-4",
      )}
    >
      <ErrorBoundary FallbackComponent={Error}>
        <Suspense fallback={<Skeleton className="h-12 w-12 rounded-full" />}>
          {children}
        </Suspense>
      </ErrorBoundary>
    </div>
  );

  return (
    <SidebarProvider className="h-svh overflow-hidden print:h-auto print:overflow-visible">
      <PageActionsProvider>
        <SidebarNavigation />
        <main
          className={cn(
            "ml-auto flex h-svh min-h-0 w-full max-w-full overflow-hidden peer-data-[state=collapsed]:w-[calc(100%-var(--sidebar-width-icon)-1rem-2px)] peer-data-[state=expanded]:w-[calc(100%-var(--sidebar-width))] sm:transition-[width] sm:duration-200 sm:ease-linear print:h-auto print:w-full print:overflow-visible",
            showDealExplorer ? "flex-row" : "flex-col",
          )}
        >
          {shellHidesGlobalHeader ? (
            <SpotlightSearchButton variant="hidden" />
          ) : null}
          {/* No floating Ask Sigma on Messages (it covered the composer). */}
          {shellHidesGlobalHeader && !isProjectShowShell && !isMessagesShell ? (
            <CrmAssistantButton variant="fab" />
          ) : null}

          {showDealExplorer ? (
            <>
              <DealsExplorerPanel currentDealId={currentDealId!} />
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                {globalHeader}
                {shellHidesGlobalHeader ? (
                  <WorkspaceTabBar placement="strip" />
                ) : null}
                <div className={mainContentPadding}>{scrollableContent}</div>
              </div>
            </>
          ) : (
            <>
              {globalHeader}
              {shellHidesGlobalHeader ? (
                <WorkspaceTabBar placement="strip" />
              ) : null}
              <div className={mainContentPadding}>{scrollableContent}</div>
            </>
          )}
        </main>
        <Notification />
      </PageActionsProvider>
    </SidebarProvider>
  );
};
